import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import questionnaireData from '../assets/Questionnaire-des-dons-db.json';

const categories = {
  1: 'Administration', 2: 'Apostolat', 3: "Relation d'aide",
  4: 'Artisanat', 5: 'Arts créatifs', 6: 'Discernement',
  7: 'Encouragement', 8: 'Évangélisation', 9: 'Foi',
  10: 'Libéralité', 11: 'Guérison', 12: 'Service',
  13: 'Hospitalité', 14: 'Connaissance', 15: 'Leadership',
  16: 'Compassion', 17: 'Miracles', 18: 'Prophétie',
  19: 'Berger/Pasteur', 20: 'Enseignement', 21: 'Sagesse',
};

const QuestionnaireReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [questionsByCategory, setQuestionsByCategory] = useState({});
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  if(answers === null){
	  console.debug("dummy")
  }

  const loadQuestionnaireData = useCallback(async () => {
    try {
      if (!isAuthenticated || !user?.id) {
        navigate('/');
        return;
      }

      // Load saved progress
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/loadProgress'
        : '/api/loadProgress';

      const response = await axios.get(apiUrl, {
        params: { userId: user.id, questionnaireId: id },
      });

      if (!response.data.progress || !response.data.progress.answers) {
        setError('Aucune progression sauvegardée trouvée');
        return;
      }

      const { answers: savedAnswers } = response.data.progress;
      setAnswers(savedAnswers);

      // Use the imported questions data
      const questions = questionnaireData;

      // Group questions by category
      const grouped = {};
      questions.forEach((question, index) => {
        const category = question.categorie;
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push({
          ...question,
          index,
          answer: savedAnswers[index]
        });
      });

      // Sort categories by name
      const sortedCategories = Object.keys(grouped)
        .sort((a, b) => categories[a].localeCompare(categories[b]))
        .reduce((acc, key) => {
          acc[key] = grouped[key];
          return acc;
        }, {});

      setQuestionsByCategory(sortedCategories);
    } catch (error) {
      console.error('Error loading questionnaire data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, id, navigate]);

  useEffect(() => {
    loadQuestionnaireData();
  }, [loadQuestionnaireData]);

  const getAnswerText = (value) => {
    if (value === 0) return 'Jamais (0)';
    if (value === 1) return 'Parfois (1)';
    if (value === 2) return 'Souvent (2)';
    if (value === 3) return 'Toujours (3)';
    return 'Non répondu';
  };

  const getAnswerColor = (value) => {
    if (value === 0) return '#e74c3c';
    if (value === 1) return '#f39c12';
    if (value === 2) return '#3498db';
    if (value === 3) return '#27ae60';
    return '#95a5a6';
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="loading">Chargement du questionnaire...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="error">{error}</div>
        <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
          Retour au tableau de bord
        </button>
      </div>
    );
  }

  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      <div className="card">
        <h1 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
          Révision du Questionnaire - Toutes les Réponses
        </h1>
        
        <div style={{ marginBottom: '2rem' }}>
          <button 
            onClick={() => navigate('/dashboard')} 
            className="btn btn-secondary"
            style={{ marginRight: '1rem' }}
          >
            ← Retour au tableau de bord
          </button>
          <button 
            onClick={() => navigate(`/results/${id}`)} 
            className="btn btn-primary"
          >
            Voir les résultats
          </button>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#2c3e50' }}>Guide de réponse</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ padding: '0.5rem', backgroundColor: '#e74c3c', color: 'white', borderRadius: '4px' }}>
              <strong>Jamais (0):</strong> Ne vous correspond pas du tout
            </div>
            <div style={{ padding: '0.5rem', backgroundColor: '#f39c12', color: 'white', borderRadius: '4px' }}>
              <strong>Parfois (1):</strong> Vous correspond un peu
            </div>
            <div style={{ padding: '0.5rem', backgroundColor: '#3498db', color: 'white', borderRadius: '4px' }}>
              <strong>Souvent (2):</strong> Vous correspond bien
            </div>
            <div style={{ padding: '0.5rem', backgroundColor: '#27ae60', color: 'white', borderRadius: '4px' }}>
              <strong>Toujours (3):</strong> Vous correspond parfaitement
            </div>
          </div>
        </div>

        {Object.entries(questionsByCategory).map(([categoryId, questions]) => (
          <div key={categoryId} style={{ marginBottom: '3rem' }}>
            <h2 style={{ 
              color: '#2c3e50', 
              borderBottom: '2px solid #3498db',
              paddingBottom: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              {categories[categoryId]}
            </h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              {questions.map((question, idx) => (
                <div 
                  key={question.index}
                  style={{
                    padding: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Question {question.index + 1}:</strong> {question.texte_question}
                  </div>
                  <div style={{ 
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: getAnswerColor(question.answer),
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}>
                    {getAnswerText(question.answer)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuestionnaireReviewPage;
